import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens/dist";
import { validate } from "@frui.ts/validation";
import { plainToClass } from "class-transformer";
import { action, computed, observable, reaction } from "mobx";
import NiaResponse from "models/niaResponse";
import EnumService from "services/enumService";
import EventBus, {
  omissionCreationFailed,
  omissionDocumentsUploadFailed,
  omissionDocumentsUploadSuccess,
  omissionISDSConceptStatusCheckFailure,
  omissionISDSConceptStatusCheckSuccess,
  omissionISDSSessionIdHandled,
  omissionNIAResponseHandled,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import NiaService, { Notice } from "services/NIA/niaService";
import OmissionService, { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "services/omissionService";
import AppRouter from "services/appRouter";
import ISDSService from "services/ISDS/isdsService";
import serializeNotice from "utils/serializeNotice";
import VehicleInfoViewModel from "../../common/vehicleInfoViewModel";
import NoticeViewModel from "../noticeViewModel";
import debounce from "debounce-promise";
import OperatorViewModel from "../components/operator/operatorViewModel";
import ApplicantPersonViewModel from "../components/applicantPersonViewModel";
import ManualEntityValidator from "@frui.ts/validation/dist/manualEntityValidator";
import Vehicle from "models/vehicle";
import { CountryIdValidationRules, LicensePlateValidationRules } from "models/orderItem";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import OutageService from "services/outageService";
import OutagesViewModel from "../../common/outagesViewModel";
import { onlyResolvesLast } from "awesome-only-resolves-last-promise/dist";
import ChargeRegistrationService from "services/chargeRegistrationService";
import { repeatIfFailed } from "services/requestRecoveryService";
import ExemptionCancellationApplicationProductionResponse from "entities/exemptionCancellationApplicationProductionResponse";
import { PersistedBase64 } from "models/notice";
import { NoticePowerOfAttorneyRules } from "models/omission";

export const OmissionNoticeNavigationName = "omission_notice";

@Router.registerRoute({ route: OmissionNoticeNavigationName, name: Router.Self })
export default class OmissionViewModel extends NoticeViewModel {
  private vehicleManualValidator: ManualEntityValidator<Vehicle>;
  @observable vehicleInfoViewModel: VehicleInfoViewModel;
  @observable applicantPersonViewModel: ApplicantPersonViewModel;
  @observable operatorViewModel: OperatorViewModel;
  @observable allowCreateExemption = false;
  private debouncedLookForChargingInstructions = debounce(this.lookForChargingInstructions, 400);
  private lastResolveFindChargingInstructions = onlyResolvesLast(this.findChargingInstructions);

  constructor(
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected niaService: NiaService,
    protected isdsService: ISDSService,
    protected outageService: OutageService,
    protected eventBus: EventBus,
    protected service: OmissionService,
    protected router: AppRouter,
    protected outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private chargeRegistrationService: ChargeRegistrationService,
    private vehicleInfoVMFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private applicantPersonVMFactory: ReturnType<typeof ApplicantPersonViewModel.Factory>,
    private operatorVMFactory: ReturnType<typeof OperatorViewModel.Factory>
  ) {
    super(
      localizationService,
      enumService,
      niaService,
      isdsService,
      outageService,
      eventBus,
      service,
      router,
      outagesViewModelFactory
    );
    this.navigationName = OmissionNoticeNavigationName;
    this.createChildren();
    this.unsubscriptions.push(
      this.eventBus.subscribe(omissionCreationFailed, this.generalOperationFailed),
      this.eventBus.subscribe(omissionDocumentsUploadFailed, this.isdsOperationFailed),
      this.eventBus.subscribe(omissionDocumentsUploadSuccess, this.isdsUploadOperationSuccess),
      this.eventBus.subscribe(omissionISDSConceptStatusCheckSuccess, this.isdsConceptStatusCheck),
      this.eventBus.subscribe(omissionISDSConceptStatusCheckFailure, this.isdsConceptFailure),
      this.eventBus.subscribe(omissionNIAResponseHandled, this.NiaResponseHandler),
      this.eventBus.subscribe(omissionISDSSessionIdHandled, this.ISDSSessionIdHandler)
    );
    this.attachValidators();
    this.setDefaultCountryId();
  }

  @computed
  get cancellationApplicationProduction(): ExemptionCancellationApplicationProductionResponse | undefined {
    return this.service.cancellationApplicationProduction;
  }

  @computed
  get isContinueDisabled(): boolean {
    return this.service.cancellationApplicationProduction?.canBeAppliedFor === false;
  }

  @bound
  translateGeneral(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`omission.${code}`, undefined, disableUnbreakableRules);
  }

  @bound
  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("omission", code);
  }

  @bound
  translateWarningReason(code: string): string {
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false);
  }

  protected async onInitialize(): Promise<any> {
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    this.service.cancellationApplicationProduction = undefined;

    this.unsubscriptions.push(
      reaction(
        () => [this.vehicleInfoViewModel.item.countryId, this.vehicleInfoViewModel.item.licensePlate],
        () => {
          this.handleVehicleInfoChange();
        },
        {
          fireImmediately: true,
        }
      )
    );

    return super.onInitialize();
  }

  protected attachValidators() {
    attachAutomaticBlurValidator(this.notice.vehicle, {
      ...LicensePlateValidationRules,
      ...CountryIdValidationRules,
    });
    attachAutomaticBlurValidator(this.notice, {
      ...NoticePowerOfAttorneyRules,
    });
  }

  @computed
  get notice() {
    return this.service.notice;
  }

  async navigate(subPath: string | undefined, params: any) {
    if (!params) this.eventBus.publish(omissionISDSSessionIdHandled({ sessionId: "" }));

    if (params.sessionId) {
      if (sessionStorage.getItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY)) {
        sessionStorage.removeItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY);
        await this.isdsService.conceptStatus(params.sessionId, Notice.OMISSION);
        return;
      }
      this.eventBus.publish(omissionISDSSessionIdHandled({ sessionId: params.sessionId }));
    } else if (params.response) {
      const response = params.response;

      if (response && this.notice && this.notice.applicant) {
        const urlDecoded = decodeURI(response);
        const base64Decoded = atob(urlDecoded);
        const niaResponse = plainToClass(NiaResponse, JSON.parse(base64Decoded));

        if (!niaResponse) return;

        this.eventBus.publish(omissionNIAResponseHandled({ niaResponse }));
      }
    }
  }

  @action.bound
  protected isdsConceptStatusCheck(data: { type: string; payload: { success: boolean; notice: Notice } }) {
    if (data.payload.success && data.payload.notice) {
      this.router.navigate("omission_documents", undefined, {
        notice: data.payload.notice,
      });
    }
  }

  @action.bound
  protected isdsUploadOperationSuccess(data: { type: string; payload: { conceptIdent: string } }) {
    super.isdsUploadOperationSuccess(data);
    this.isdsService.redirectToISDSConcept(Notice.OMISSION, data.payload.conceptIdent);
  }

  @bound
  private async handleVehicleInfoChange() {
    this.allowCreateExemption = false;
    this.service.cancellationApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
  }

  @bound
  private findChargingInstructions(countryId: string, licensePlate: string) {
    return this.chargeRegistrationService.getChargingInstructions(countryId, licensePlate);
  }

  @bound
  protected async persistNotice() {
    sessionStorage.setItem(Notice.OMISSION, serializeNotice(this.notice));

    await super.persistNotice();
  }

  @action.bound
  @repeatIfFailed
  private async lookForChargingInstructions() {
    const countryId = this.notice.vehicle?.countryId;
    const licensePlate = this.notice.vehicle?.licensePlate;

    if (!countryId || !licensePlate) return;

    try {
      await this.lastResolveFindChargingInstructions(countryId, licensePlate);
    } catch (e) {
      throw new Error("Vehicle instructions check failed");
    }
  }

  @action.bound
  public async createNotice(): Promise<string | undefined> {
    this.errorMessage = undefined;
    this.persistNotice();
    if (this.vehicleManualValidator) {
      this.vehicleManualValidator.removeError("licensePlate");
    }
    if (this.notice?.vehicle?.countryId && this.notice?.vehicle?.licensePlate && validate(this.notice.vehicle)) {
      this.notice.vehicle.countryId = this.vehicleInfoViewModel.item.countryId;
      this.notice.vehicle.licensePlate = this.vehicleInfoViewModel.item.licensePlate;

      const response = (await this.service.getProduction(this.notice)) as any;

      if (response.errors) {
        this.creationLoadingModalToggle(false);
        this.errorMessage = this.localizationService.translateGeneral("errors.invalidData");
        return;
      }

      if (this.service.cancellationApplicationProduction?.rejectionReason?.length && !this.allowCreateExemption) {
        this.allowCreateExemption = true;

        return Promise.resolve(undefined);
      }
      return this.service.createOmission(this.notice);
    }
  }

  @action.bound
  public uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]): Promise<void> | undefined {
    this.errorMessage = undefined;
    return this.service.uploadDocuments(ISDSSessionId, files);
  }

  @action.bound
  public redirectToISDS(): void {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.isdsService.redirectToISDS(Notice.OMISSION);
  }

  @action.bound
  public redirectToNia() {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.niaService.redirectToNia(Notice.OMISSION);
  }

  protected createChildren() {
    this.children.clear();
    this.children.push(
      (this.vehicleInfoViewModel = this.vehicleInfoVMFactory(this.notice.vehicle!, {
        attachValidator: false,
        fixedLicensePlateSize: false,
        initByLocalization: true,
      })),
      (this.operatorViewModel = this.operatorVMFactory(this.notice, "omission", this.persistNotice, this.showBirthdate())),
      (this.applicantPersonViewModel = this.applicantPersonVMFactory(this.notice.applicant, undefined, this.showBirthdate()))
    );
  }
}
